import { useState, useCallback, forwardRef, useImperativeHandle, useEffect} from 'react';
import { differenceInDays } from 'date-fns'; 
import {getDateDifference} from './CorrectedAgeManager/correctedAgeUtils';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './Calculator.scss';
import { Form, Input, Button } from "@heroui/react";
import {Card, CardHeader, CardBody, CardFooter} from "@heroui/card";
import refreshIcon from '../../assets/refreshCcw.svg';
import alertCircleIcon from '../../assets/alert-circle.svg';

const FULL_TERM_WEEKS = 40;
const DAYS_IN_WEEK = 7;

interface Props {
  gestationWeeks: string;
  setGestationWeeks: (weeks: string) => void;
  setRecommendedCheckDate: (date: string) => void;
  setRecommendedSupportWorkshopDate: (date: string) => void;
  setFixedAgeInWeeks: (weeks: number | '') => void;
  setRecommendedAbrStartDate: (date: string) => void;
  setRecommendedAbrEndDate: (date: string) => void;
  setRecommendedHearingTest1StartDate: (date: string) => void;
  setRecommendedHearingTest1EndDate: (date: string) => void;
  setRecommendedHearingTest2Date: (date: string) => void;
  setRecommendedHearingTest3Date: (date: string) => void;
  birthDate: Date | null;
  setBirthDate: (date: Date | null) => void;
  onCalculate: (calculated: boolean) => void;
  onInputChange: (isDirty: boolean) => void;
  currentDate?: Date;
  onReset?: () => void;
}

export interface CalculatorRef {
  resetInputs: () => void;
}

const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDatePlusMonths = (date: Date, months: number): string => {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + months);
  const day = String(newDate.getDate()).padStart(2, '0');
  const month = String(newDate.getMonth() + 1).padStart(2, '0');
  const year = newDate.getFullYear();
  return `${day}/${month}/${year}`;
}


const CorrectedAgeCalculator = forwardRef<CalculatorRef, Props>(({
  gestationWeeks,
  setGestationWeeks,
  setRecommendedCheckDate,
  setRecommendedSupportWorkshopDate,
  setRecommendedAbrStartDate,
  setRecommendedAbrEndDate,
  setRecommendedHearingTest1StartDate,
  setRecommendedHearingTest1EndDate,
  setRecommendedHearingTest2Date,
  setRecommendedHearingTest3Date,
  setFixedAgeInWeeks,
  birthDate,  
  setBirthDate,
  onCalculate,
  onInputChange,
  onReset,
  currentDate = new Date(),
}, ref) => {
  const [gestationDays, setGestationDays] = useState<string>('');
  const [ageToShow, setAgeToShow] = useState<string>('');
  const [chronologicalAgetoShow, setChronologicalAgetoShow] = useState<string>('');
  const [isCalculated, setIsCalculated] = useState<boolean>(false);
  const [isFirstTimeCalculated, setIsFirstTimeCalculated] = useState<boolean>(false);
  const [showWeekValidation, setShowWeekValidation] = useState(false);
  const [showDayValidation, setShowDayValidation] = useState(false);
  const [showForm, setShowForm] = useState<boolean>(true);

  useEffect(() => {    
    onInputChange(!isCalculated);
  }, [birthDate, gestationWeeks, gestationDays, onInputChange]);

  useImperativeHandle(ref, () => ({
    resetInputs: () => {
      setBirthDate(null);
      setGestationWeeks('');
      setGestationDays('');
      setAgeToShow('');
      setChronologicalAgetoShow('');
      setIsCalculated(false);
      setShowWeekValidation(false);
      setShowDayValidation(false);
      onCalculate(false);
      setIsFirstTimeCalculated(false);
      setShowForm(true);
    }
  }));
  

  const isInputValid = useCallback(() => {
    const weeks = parseInt(gestationWeeks);
    const days = parseInt(gestationDays);
    return (
      birthDate !== null &&
      !isNaN(weeks) &&
      weeks >= 20 &&
      weeks <= 44 &&
      ((!isNaN(days) &&
      days >= 0 &&
      days <= 6)||(isNaN(days)))
    );
  }, [birthDate, gestationWeeks, gestationDays]);

  useEffect(() => {
    setAgeToShow('');
    setChronologicalAgetoShow('');
    setIsCalculated(false);
    setShowWeekValidation(false);
    setShowDayValidation(false);
    setRecommendedCheckDate('');
    setRecommendedSupportWorkshopDate('');
    setFixedAgeInWeeks('');
  }, [birthDate, gestationWeeks, gestationDays]);

  // Validation message getters
  const getWeekValidationMessage = () => {
    const weeks = parseInt(gestationWeeks);
    if (gestationWeeks && (isNaN(weeks) || weeks < 20 || weeks > 44)) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: 0 }}>
          <img
            src={alertCircleIcon}
            alt="Alert"
            style={{ width: '1rem', height: '1rem', marginRight: '4px' }}
          />
          <span className="error-message">
            ערך בין 20-44
          </span>
        </div>
      );
    }
    return null;
  };

  const getDayValidationMessage = () => {
    const days = parseInt(gestationDays);
    if (gestationDays && (isNaN(days) || days < 0 || days > 6)) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: 0 }}>
          <img
            src={alertCircleIcon}
            alt="Alert"
            style={{ width: '1rem', height: '1rem', marginRight: '4px' }}
          />
          <span className="error-message">
            ערך בין 0-6
          </span>
        </div>
      );
    }
    return null;
  };


  const calculateAge = (
    birthDate: Date | null,
    gestationWeeks: string,
    gestationDays: string
  ): void => {
    if (!birthDate || !gestationWeeks) return;
    const weeks = parseInt(gestationWeeks);
    const days = parseInt(gestationDays) || 0;

    const today = currentDate;
    const birth = new Date(birthDate);

    const totalGestationDays = weeks * DAYS_IN_WEEK + days;
    const daysPremature = FULL_TERM_WEEKS * DAYS_IN_WEEK - totalGestationDays;

    const chronologicalAgeDays = differenceInDays(today, birth);
    let chronologicalAgeString = getDateDifference(today, birth, 1);
    const correctedAgeDays = chronologicalAgeDays - daysPremature;    
    setChronologicalAgetoShow(chronologicalAgeString);

    const totalDaysPregnant = totalGestationDays + chronologicalAgeDays;

    if (weeks >= 37) {
      setAgeToShow(`התינוק אינו פג, ניתן להשתמש בגיל הכרונולוגי (${chronologicalAgeString})`);
      setIsCalculated(true);
      setRecommendedCheckDate('');
      setRecommendedSupportWorkshopDate('');
      onCalculate(true);
      setIsFirstTimeCalculated(true);
      setShowForm(false);
      return;
    }

    if (correctedAgeDays > 0){
      let ageString = getDateDifference(today, addDays (birth, daysPremature), 3);
      setAgeToShow(ageString);
    } else if (correctedAgeDays === 0) {
      setAgeToShow('0 ימים');
    } else {
      const currentWeeks = Math.floor(totalDaysPregnant / DAYS_IN_WEEK);
      const currentDays = totalDaysPregnant % DAYS_IN_WEEK;
      setAgeToShow(`שבוע ${currentDays}+${currentWeeks} להריון`);
    }
    // calculate the checkup date, workshop date and abr start/end dates (startABRdate)
    // start/end 7/8 months (hearingTest1), start 1.5 years (hearingTest2) , start 2.5 chronological years (hearingTest3)
    const checkupDate = addDays(birthDate, (FULL_TERM_WEEKS - weeks) * 7 - days + 42);   //  6 weeks after corrected age
    const workshopDate = addDays(birthDate, (FULL_TERM_WEEKS - weeks) * 7 - days + 70);  // 10 weeks after corrected age
    const startABRdate = addMonths(addDays(birthDate, (FULL_TERM_WEEKS - weeks) * 7 - days), 1);  
    const hearingTest1date = addMonths(addDays(birthDate, (FULL_TERM_WEEKS - weeks) * 7 - days), 7);
    const hearingTest2date = addMonths(addDays(birthDate, (FULL_TERM_WEEKS - weeks) * 7 - days), 18);
    const hearingTest3date = addMonths(addDays(birthDate, 0), 30);
    setRecommendedCheckDate(formatDate(checkupDate));
    setRecommendedSupportWorkshopDate(formatDate(workshopDate));
    setRecommendedAbrStartDate(formatDate(startABRdate));
    setRecommendedAbrEndDate(formatDatePlusMonths(startABRdate,2)); // gives 2 months range from the start date
    setRecommendedHearingTest1StartDate(formatDate(hearingTest1date));
    setRecommendedHearingTest1EndDate(formatDatePlusMonths(hearingTest1date,1)); // gives 1 month range from the start date
    setRecommendedHearingTest2Date(formatDate(hearingTest2date));
    setRecommendedHearingTest3Date(formatDate(hearingTest3date));    
    setIsFirstTimeCalculated(true);
    onCalculate(true);
    setIsCalculated(true);
    setShowForm(false);
    if (correctedAgeDays <= 70) {
      const fa = Math.round(totalDaysPregnant / DAYS_IN_WEEK);
      setFixedAgeInWeeks(fa);            
      return;
    }

    const fa = Math.ceil(FULL_TERM_WEEKS+(correctedAgeDays / DAYS_IN_WEEK));
    setFixedAgeInWeeks(fa);
    setAgeToShow(getDateDifference(today, addDays (birth, daysPremature), 3));

  };

  const weekErrorMessage = getWeekValidationMessage();
  const dayErrorMessage = getDayValidationMessage();

  const hasWeekError = showWeekValidation && !!weekErrorMessage;
  const hasDayError = showDayValidation && !!dayErrorMessage;

  return (
  <div className='corrected-age-calculator'>
    {showForm && (
    <div>      
      <Card
        radius="sm"
        className="w-full"
        style={{
          borderRadius: 'var(--radius-m, 8px)',
          background: 'var(--neutral-light, #EEFFFD)', 
          boxShadow:
            '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)',
        }}
      >
        <CardBody className="space-y-4" style={{ padding: '1rem' }}>
          <div className="max-w-sm mx-auto" style={{ width: '100%', maxWidth: '28rem' }}>
            <Form className="calculator-layout" validationBehavior="aria">
              <div className="inputs-section">
                <div className="form-row mb-2" id="birthDate" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <label style={{ minWidth: '100px', textAlign: 'right', paddingRight: '10px', color: '#0C3058', flexShrink: 0}}>תאריך לידה</label>
                  <div style={{ flex: '1', minWidth: '200px', maxWidth: '250px' }}>
                    <DatePicker
                        selected={birthDate}
                        onChange={(date) => setBirthDate(date)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="בחירה תאריך"
                        className="form-control"
                        maxDate={currentDate}
                        popperProps={{ strategy: 'fixed' }}
                        customInput={<input data-testid="calculator-birthDate" />}
                    />
                  </div>
                </div>
                  <div
                  className="form-row mb-2"
                  id="gestationWeek"
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}
                >
                  <label
                    style={{
                      minWidth: '100px',
                      textAlign: 'right',
                      paddingRight: '10px',
                      color: '#0C3058',
                      marginTop: '8px',
                      flexShrink: 0
                    }}
                  >
                    שבוע לידה
                  </label>

                  <div style={{ flex: '1', minWidth: '100px', maxWidth: '120px', display: 'flex', flexDirection: 'column' }}>
                    <Input
                      type="number"
                      inputMode="numeric"
                      data-testid="calculator-gestationWeek"
                      min={20}
                      max={44}
                      value={gestationWeeks}
                      onChange={(e) => {
                        setGestationWeeks(e.target.value);
                        setShowWeekValidation(false);
                      }}
                      onInvalid={(e) => e.preventDefault()}
                      onFocus={(e) => e.target.select()}
                      onBlur={() => setShowWeekValidation(true)}
                      placeholder="שבוע"
                      style={{ width: '100%' }}
                      classNames={{
                        inputWrapper: `
                          bg-white border rounded-sm
                          hover:border-blue-600 focus-within:border-blue-700
                          ${hasWeekError ? 'input-error-border' : 'border-blue-500'}
                        `,
                        input: "bg-white",
                      }}
                    />

                    <div
                      data-testid="calculator-week-error"
                      className="text-error"
                      style={{
                        height: '1rem',
                        marginTop: '2px',
                        fontSize: '0.75rem',
                      }}
                    >
                      {hasWeekError && weekErrorMessage}
                    </div>
                  </div>

                  <div style={{ flex: '1', minWidth: '100px', maxWidth: '120px', display: 'flex', flexDirection: 'column' }}>
                    <Input
                      type="number"
                      inputMode="numeric"
                      data-testid="calculator-gestationDays"
                      min={0}
                      max={6}
                      value={gestationDays}
                      onChange={(e) => {
                        setGestationDays(e.target.value);
                        setShowDayValidation(false);
                      }}
                      onBlur={() => setShowDayValidation(true)}
                      onFocus={(e) => e.target.select()}
                      placeholder="+ימים"
                      style={{ width: '100%' }}
                      classNames={{
                        inputWrapper: `
                          bg-white border rounded-sm
                          hover:border-blue-600 focus-within:border-blue-700
                          ${hasDayError ? 'input-error-border' : 'border-blue-500'}
                        `,
                        input: "bg-white",
                      }}
                    />
                    <div
                      data-testid="calculator-day-error"
                      className="text-error"
                      style={{
                        height: '1rem',
                        marginTop: '2px',
                        fontSize: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {hasDayError && dayErrorMessage}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap', flexDirection: 'row-reverse' }}>
                <div style={{ minWidth: '85px', flexShrink: 0 }} className="button-spacer"></div>
                <Button
                    type="button"
                    className="btn-custom-green"
                    data-track-id="corrected_age_clean"
                    style={{
                      backgroundColor: 'white',
                      color: '#103C6E',
                      flex: '1',
                      minWidth: '125px',
                      height: '48px',
                      fontSize: '1.25rem',
                      fontWeight: '500',
                      border: '1px solid #103C6E',
                      borderRadius: '4px'
                    }}
                    onClick={() => {
                      if (ref && typeof ref !== 'function' && ref.current) {
                        ref.current.resetInputs();
                      }
                      if (onReset) onReset();
                    }}
                  >
                  נקה
                </Button>
                <Button
                  type="button"
                  data-testid="calculator-calc-btn"
                  className="btn-custom-green"
                  data-track-id="corrected_age_continue"                 
                  style={{
                    backgroundColor: "#103C6E",
                    color: "white",
                    flex: '1',
                    minWidth: '125px',
                    height: "48px",
                    fontSize: "1.25rem",
                    fontWeight: "500",
                  }}
                  onClick={() => calculateAge(birthDate, gestationWeeks, gestationDays)}
                  disabled={!isInputValid()}
                >
                  המשך
                </Button>
              </div>
            </Form>
          </div>
        </CardBody>
      </Card>
    </div>
  )}

  {!showForm && (
      <div>
        <Card
          radius="sm"
          shadow="none"
          className="w-full"
          style={{
            borderRadius: '8px',
            border: '1px solid var(--border-default, #5878A4)',
            background: 'var(--neutral-light, #EEFFFD)'
          }}
        >
          <CardBody className="space-y-4">
              {isFirstTimeCalculated && (
                <div className={`result-container ${isCalculated ? '' : 'blurred'}`}>
                  <p data-testid="calculator-age-result" className="result-text-bold"> {ageToShow}</p>
                  {(parseInt(gestationWeeks) < 37 &&
                    <p className="result-text">גיל כרונולוגי: {chronologicalAgetoShow}</p>
                  )}
                </div>
              )}
          </CardBody>
        </Card>
        <div
          className="user-inputs"
          style={{
            marginTop: '1rem',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
            padding: '0 10px'
          }}
        >
          <p style={{ margin: 0, textAlign: 'center', wordBreak: 'break-word' }}>
            לפי: תאריך לידה: {birthDate ? birthDate.toLocaleDateString('he-IL') : ''}
            {' '}שבוע לידה: {gestationDays}+{gestationWeeks}
          </p>

          <button
            type="button"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }}
            onClick={() => {
              setShowForm(true);

              if (ref && typeof ref !== 'function' && ref.current) {
                ref.current.resetInputs();
              }
              onReset?.();
            }}
          >
            <img
              src={refreshIcon}
              alt="Refresh"
              style={{ width: '1.2rem', height: '1.2rem' }}
            />
          </button>
        </div>
      </div>
    )}
  </div>
  );
});

export default CorrectedAgeCalculator;
